import React, { useContext } from 'react';

import { AppType } from '../../../../store/store';
import css from '../SetPlan.module.scss';
import { LearnMoreTopic, Plan } from '../index';
import { AnalyticsContext } from '../../../../ModularReduxAnalytics';
import Plans from '../Plans/Plans';
import { MAYBE_LATER, NO_PERKS } from '../../../App/constants';
import { getSetPlanPromoConfig } from '../../../../helpers/promotions';
import { useToggles } from '../../../../state/toggles';
import { PlanPrices, planPrices, SavingsRewards } from '../../../../utils/prices';

interface Props {
  app: AppType;
  plans: PlanPrices;
  savings: SavingsRewards
  featureToggles: any;
  user: any;
  onInfoClicked: (plan: Plan) => void;
  onBackClicked: () => void;
  onLearnMoreClicked: (learnMoreTopic: LearnMoreTopic, plan: Plan) => void;
  onUpgradeClicked: (plan: Plan) => void;
  onSkip: () => void;
}

interface FeatureToggles {
  corePlanCopyEnabled: boolean;
}

const subHeaderCopy = (savings: string) => [
  `Greenlight kids can earn at least ${savings} on Savings with all Greenlight plans. Earn even more with Greenlight Infinity and Max. All plans cover the whole family, up to 5 kids.`
];
const newHeaderCopy = (price: number) => [
  `Start your 1-month free Greenlight Core trial ($${price}/month after).`,
  'Or upgrade to earn up to 5% on savings*, investing for kids, family safety features, and more. All plans cover up to 5 kids.'
];

const PlansContainerWithMaxAndInfinityDeafault = ({
  app,
  user,
  onInfoClicked,
  onLearnMoreClicked,
  onUpgradeClicked,
  onBackClicked,
  corePlanCopyEnabled,
  plans,
  savings
}: Props & FeatureToggles) => {
  const { promotionId } = user;
  const { loading } = app;

  const promoConfig = getSetPlanPromoConfig(promotionId || '');
  const { analytics } = useContext(AnalyticsContext);


  let subHeader = corePlanCopyEnabled
    ? newHeaderCopy(plans.standard)
    : subHeaderCopy(savings.core);
  if (promoConfig.subheader) {
    subHeader = [promoConfig.subheader];
  }

  return (
    <>
      <h1 data-e2e="upgrade-plans-header">
        {promoConfig.header || 'Get more from Greenlight.'}
      </h1>
      <h2
        data-testid="upgrade-plans-subheader"
        data-e2e="upgrade-plans-subheader"
      >
        {subHeader.map((s, idx) => (
          <p key={`sub-header-${idx}`}>{s}</p>
        ))}
      </h2>
      <Plans
        onBackClicked={onBackClicked}
        isMaxOrInfinityXP={true}
        onInfoClicked={onInfoClicked}
        plans={plans}
        onLearnMoreClicked={onLearnMoreClicked}
        loading={loading}
        analytics={analytics}
        onUpgradeClicked={onUpgradeClicked}
        flowId={app.flowId ?? ''}
        promoConfig={promoConfig}
        savings={savings}
      />
      <p
        className={`${css.investSecondaryButton} ${css.investSecondaryButtonNew}`}
        onClick={onBackClicked}
      >
        Go back
      </p>
      <div className={css.investFooter}>
        <p>
          &dagger;Requires mobile data or a WiFi connection, and access to
          sensory and motion data from cell phone to utilize family location
          sharing, SOS alerts and crash detection features. Messaging and data
          rates and other terms may apply.
        </p>
        <p>
          *See Greenlight's Terms of Service for details on earnings limits and
          how to qualify. Subject to change at any time.
        </p>
        <p>
          **Cell phone and purchase protection provided by Virginia Surety
          Company Inc. Cell phone protection is not available to residents of
          New York.
        </p>
        <p>
          Depending on where you live, you may be charged taxes in addition to
          your subscription price.
        </p>
        <p>
          By continuing, you are accepting Greenlight’s{' '}
          <a
            target="_blank"
            href={`https://greenlight.com/terms-of-service`}
            data-e2e="terms"
            rel="noreferrer"
          >
            Terms of Service
          </a>{' '}
          &{' '}
          <a
            target="_blank"
            href={`https://greenlight.com/privacy`}
            data-e2e="privacy-policy"
            rel="noreferrer"
          >
            Privacy Statement
          </a>
          .
        </p>
      </div>
    </>
  );
};

const PlansContainerControl = ({
  app,
  user,
  onSkip,
  onInfoClicked,
  onLearnMoreClicked,
  onUpgradeClicked,
  onBackClicked,
  corePlanCopyEnabled,
  plans,
  savings
}: Props & FeatureToggles) => {
  const { promotionId, partner } = user;
  const { loading } = app;

  const { acquisitionSetPlanMaybeLater, riskFreePaintedDoor } = useToggles();
  const maybeLaterText = acquisitionSetPlanMaybeLater.enabled
    ? MAYBE_LATER
    : NO_PERKS;
  const promoConfig = getSetPlanPromoConfig(promotionId || '');
  const { analytics } = useContext(AnalyticsContext);

  let subHeader = corePlanCopyEnabled
    ? newHeaderCopy(plans.standard)
    : subHeaderCopy(savings.core);
  if (promoConfig.subheader) {
    subHeader = [promoConfig.subheader];
  }

  return (
    <>
      <h1 data-e2e="upgrade-plans-header">
        {promoConfig.header || 'Get more from Greenlight.'}
      </h1>
      <h2
        data-testid="upgrade-plans-subheader"
        data-e2e="upgrade-plans-subheader"
      >
        {subHeader.map((s, idx) => (
          <p key={`sub-header-${idx}`}>{s}</p>
        ))}
      </h2>
      <Plans
        onBackClicked={onBackClicked}
        isMaxOrInfinityXP={false}
        onInfoClicked={onInfoClicked}
        plans={plans}
        onLearnMoreClicked={onLearnMoreClicked}
        loading={loading}
        analytics={analytics}
        onUpgradeClicked={onUpgradeClicked}
        flowId={app.flowId ?? ''}
        promoConfig={promoConfig}
        savings={savings}
        riskFreePaintedDoorEnabled={riskFreePaintedDoor.enabled && !partner}
      />
      <p
        className={css.investSecondaryButton}
        data-testid="skip-upgrade"
        onClick={() => {
          analytics.trackEvent('Registration Invest Plan Action', {
            eventProps: {
              skipped: true
            }
          });
          onSkip();
        }}
      >
        {maybeLaterText}
      </p>

      <div className={css.investFooter}>
        {
          riskFreePaintedDoor.enabled && !partner && (
            <>
              <p>
                If you don't love Greenlight, simply request a 
                refund within 30 days of starting your subscription — and get your 
                money back.
              </p>
              <p>
                Valid for new customers only. Subject to identity 
                verification and minimum load requirements. Your first monthly fee 
                will be billed to your parent wallet seven days after successful 
                registration. To receive a refund of your first monthly fee, you 
                must request to close your account on or before the day immediately 
                preceding your first Monthly Billing Date. See the ‘Account’ tab of 
                Settings by tapping the gear icon on the Greenlight app home page to 
                confirm when your risk-free trial ends. See{' '}
                <a
                  href="https://greenlight.com/terms-of-service"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms
                </a>{' '}for details.
              </p>
          </>
          )
        }
        <p>
          &dagger;Requires mobile data or a WiFi connection, and access to
          sensory and motion data from cell phone to utilize family location
          sharing, SOS alerts and crash detection features. Messaging and data
          rates and other terms may apply.
        </p>
        <p>
          ^Subject to minimum balance requirements and identity verification.
          Plans start at ${plans.standard} after the first month. Offer subject
          to change at any time.
        </p>
        <p>
          *See Greenlight's Terms of Service for details on earnings limits and
          how to qualify. Subject to change at any time.
        </p>
        <p>
          **Cell phone and purchase protection provided by Virginia Surety
          Company Inc. Cell phone protection is not available to residents of
          New York.
        </p>
        <p>
          Depending on where you live, you may be charged taxes in addition to
          your subscription price.
        </p>
        <p>
          By choosing to upgrade, you are accepting Greenlight’s{' '}
          <a
            target="_blank"
            href={`https://greenlight.com/terms-of-service`}
            data-e2e="terms"
            rel="noreferrer"
          >
            Terms of Service
          </a>{' '}
          &{' '}
          <a
            target="_blank"
            href={`https://greenlight.com/privacy`}
            data-e2e="privacy-policy"
            rel="noreferrer"
          >
            Privacy Statement
          </a>
          .
        </p>
      </div>
    </>
  );
};

const PlansContainer = (props: Props) => {
  const { defaultInfinity, defaultMax, corePlanCopy } = useToggles();

  return defaultInfinity.enabled || defaultMax.enabled
    ? PlansContainerWithMaxAndInfinityDeafault({
        ...props,
        corePlanCopyEnabled: corePlanCopy.enabled
      })
    : PlansContainerControl({
        ...props,
        corePlanCopyEnabled: corePlanCopy.enabled
      });
};

export default PlansContainer;
